'use client';

import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

interface HeroVideoProps {
  src: string;
  poster: string;
  className?: string;
  filter?: string;
  lazyLoad?: boolean;
  priority?: boolean;
}

export default function HeroVideo({
  src,
  poster,
  className = 'hero-background-video',
  filter = 'brightness(0.8) contrast(1.2) saturate(1.3)',
  lazyLoad = false,
  priority,
}: HeroVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const retryTimeout = useRef<NodeJS.Timeout>(null);

  // Determine if image should have priority (default to !lazyLoad if not specified)
  const shouldPriority = priority !== undefined ? priority : !lazyLoad;

  useEffect(() => {
    if (!src) return;

    const video = videoRef.current;
    if (!video) return;

    // Force standard autoplay attributes
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    // The autoplay attribute is now handled by autoPlay={!lazyLoad} on the video tag
    video.muted = true;

    const onPlay = () => setIsPlaying(true);
    const onTimeUpdate = () => {
      if (video.currentTime > 0.1 && !isPlaying) setIsPlaying(true);
    };

    video.addEventListener('playing', onPlay);
    video.addEventListener('timeupdate', onTimeUpdate);

    const attemptPlay = async () => {
      if (!video) return;
      if (!video.paused && !video.ended && video.readyState > 2) return;

      try {
        // Essential: resetting muted ensures browser doesn't block due to "unmuted" state
        video.muted = true;
        await video.play();
      } catch (e) {
        // console.warn('HeroVideo: Autoplay blocked/failed. Retrying...', e);
        // Retry indefinitely until success (or unmount)
        retryTimeout.current = setTimeout(attemptPlay, 2500);
      }
    };

    if (lazyLoad) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            video.preload = 'auto';
            attemptPlay();
            observer.disconnect();
          }
        },
        { rootMargin: '200px' }
      );
      observer.observe(video);
      return () => observer.disconnect();
    }
    // Main hero: try immediately, even if readyState is low (it returns promise)
    attemptPlay();

    return () => {
      video.removeEventListener('playing', onPlay);
      video.removeEventListener('timeupdate', onTimeUpdate);
      if (retryTimeout.current) clearTimeout(retryTimeout.current);
    };
  }, [lazyLoad, src, isPlaying]);

  return (
    <div
      className="relative h-full w-full overflow-hidden bg-black"
      style={{ filter: filter }}
      // Fallback click handler
      onClick={() => {
        if (src && videoRef.current && !isPlaying) {
          videoRef.current.play().catch((e) => console.error('Click play failed', e));
        }
      }}
    >
      {/* 
        Video Layer (Bottom) 
      */}
      {src && (
        <video
          ref={videoRef}
          autoPlay={!lazyLoad}
          loop
          muted
          playsInline
          preload={lazyLoad ? 'none' : 'auto'}
          className="absolute inset-0 h-full w-full object-cover"
          style={{
            opacity: 1,
          }}
        >
          <source src={src} type="video/mp4" />
          <track kind="captions" srcLang="en" label="English" />
        </video>
      )}

      {/* 
        Poster Overlay (Top)
      */}
      {poster && (
        <div
          className="pointer-events-none absolute inset-0 z-10 h-full w-full transition-opacity duration-700 ease-out"
          style={{
            opacity: src && isPlaying ? 0 : 1,
          }}
        >
          <Image
            src={poster}
            alt=""
            fill
            sizes="100vw"
            className="object-cover"
            priority={true}
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  );
}
